import { useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import AnimatedCard from "./AnimatedCard";
import { type CardData } from "./Card";

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 38 31" fill="none" className={cn("shrink-0", className)}>
      <path d="M31.35 26.6H6.65C6.1275 26.6 5.7 27.0275 5.7 27.55V29.45C5.7 29.9725 6.1275 30.4 6.65 30.4H31.35C31.8725 30.4 32.3 29.9725 32.3 29.45V27.55C32.3 27.0275 31.8725 26.6 31.35 26.6ZM35.15 7.6C33.5766 7.6 32.3 8.87656 32.3 10.45C32.3 10.8716 32.395 11.2634 32.5613 11.6256L28.2625 14.2025C27.3481 14.7487 26.1666 14.44 25.6381 13.5138L20.7991 5.04688C21.4344 4.52437 21.85 3.74062 21.85 2.85C21.85 1.27656 20.5734 0 19 0C17.4266 0 16.15 1.27656 16.15 2.85C16.15 3.74062 16.5656 4.52437 17.2009 5.04688L12.3619 13.5138C11.8334 14.44 10.6459 14.7487 9.7375 14.2025L5.44469 11.6256C5.605 11.2694 5.70594 10.8716 5.70594 10.45C5.70594 8.87656 4.42937 7.6 2.85594 7.6C1.2825 7.6 0 8.87656 0 10.45C0 12.0234 1.27656 13.3 2.85 13.3C3.00437 13.3 3.15875 13.2762 3.30719 13.2525L7.6 24.7H30.4L34.6928 13.2525C34.8412 13.2762 34.9956 13.3 35.15 13.3C36.7234 13.3 38 12.0234 38 10.45C38 8.87656 36.7234 7.6 35.15 7.6Z" fill="#E1FF00" />
    </svg>
  );
}

export interface PlayerData {
  username: string;
  status: string;
  connected: boolean;
  number_cards: CardData[];
  modifier_cards: CardData[];
  action_cards: CardData[];
  round_score: number;
  unique_number_count: number;
  autoplay_mode: "safe" | "aggressive" | null;
}

interface PlayerAreaProps {
  player: PlayerData;
  isCurrentTurn: boolean;
  totalScore: number;
  compact?: boolean;
  isLeader?: boolean;
  isTargetable?: boolean;
  targetableCardType?: string | null;
}

function useNewCardStart(length: number): number {
  const prevLength = useRef<number | null>(null);

  let newStart = length;
  if (prevLength.current !== null && length > prevLength.current) {
    newStart = prevLength.current;
  } else if (prevLength.current !== null && length < prevLength.current) {
    newStart = length;
  }

  useEffect(() => {
    prevLength.current = length;
  });

  return newStart;
}

const TARGETABLE_GLOW: Record<string, string> = {
  freeze: "ring-blue-400/60 shadow-[0_0_12px_rgba(96,165,250,0.4)]",
  flip_three: "ring-red-400/60 shadow-[0_0_12px_rgba(248,113,113,0.4)]",
  second_chance: "ring-amber-400/60 shadow-[0_0_12px_rgba(251,191,36,0.4)]",
};

const TARGETABLE_OVERLAY: Record<string, { bg: string; text: string; label: string }> = {
  freeze: { bg: "bg-blue-950/60", text: "text-blue-200", label: "❄ Selecting Freeze target..." },
  flip_three: { bg: "bg-red-950/60", text: "text-red-200", label: "🃏 Selecting Flip Three target..." },
  second_chance: { bg: "bg-amber-950/60", text: "text-amber-200", label: "✨ Selecting Second Chance target..." },
};

export default function PlayerArea({ player, isCurrentTurn, totalScore, compact = false, isLeader = false, isTargetable = false, targetableCardType = null }: PlayerAreaProps) {
  const newNumberStart = useNewCardStart(player.number_cards.length);
  const newModifierStart = useNewCardStart(player.modifier_cards.length);
  const newActionStart = useNewCardStart(player.action_cards.length);
  const isBusted = player.status === "busted";
  const isStayed = player.status === "stayed";
  const isFrozen = isStayed &&
    player.action_cards.some((c) => c.subtype === "freeze");
  const isDisconnected = !player.connected;

  return (
    <div
      className={cn(
        "rounded transition-all relative flex",
        compact ? "p-2 gap-2" : "p-4 gap-3",
        isCurrentTurn
          ? "bg-green-900/40 ring-[3px] ring-[#FFFF00]"
          : "bg-black/20",
        isBusted && "bg-[#5c2e15]",
        isFrozen && "bg-[#15445c]",
        isTargetable && !isCurrentTurn && "ring-[3px] animate-pulse",
        isTargetable && !isCurrentTurn && targetableCardType && TARGETABLE_GLOW[targetableCardType]
      )}
    >
      <div className={cn("flex-1 min-w-0 relative z-20", (isBusted || isFrozen) && "opacity-30", isTargetable && "opacity-40")}>
        {compact ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={cn(
                "w-1.5 h-1.5 rounded-full shrink-0",
                player.connected ? "bg-green-400" : "bg-yellow-500"
              )}
            />
            <span className="text-[10px] font-bold text-white truncate max-w-[60px]">
              {player.autoplay_mode && "🤖 "}{player.username}
            </span>
            {isLeader && <CrownIcon className="w-3 h-3 -translate-y-px" />}
            {player.action_cards.length > 0 && (
              <>
                {player.action_cards.map((card, i) => (
                  <AnimatedCard key={`a-${i}`} card={card} mini shouldDeal={i >= newActionStart} />
                ))}
                {(player.number_cards.length > 0 || player.modifier_cards.length > 0) && (
                  <span className="w-px h-5 bg-green-300/30" />
                )}
              </>
            )}
            {player.modifier_cards.map((card, i) => (
              <AnimatedCard key={`m-${i}`} card={card} mini shouldDeal={i >= newModifierStart} />
            ))}
            {player.number_cards.map((card, i) => (
              <AnimatedCard key={`n-${i}`} card={card} mini shouldDeal={i >= newNumberStart} />
            ))}
            {player.number_cards.length === 0 &&
              player.modifier_cards.length === 0 &&
              player.action_cards.length === 0 && (
                <span className="text-[8px] text-green-300/50 italic">No cards</span>
              )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <span
                className={cn(
                  "w-2.5 h-2.5 rounded-full shrink-0",
                  player.connected ? "bg-green-400" : "bg-yellow-500"
                )}
              />
              <span className="text-sm md:text-base font-bold text-white truncate">
                {player.autoplay_mode && "🤖 "}{player.username}
              </span>
              {isLeader && <CrownIcon className="w-4 h-4 -translate-y-px" />}
            </div>
            <div className="flex flex-wrap gap-1.5 items-start min-h-16 md:min-h-20">
              {player.action_cards.length > 0 && (
                <>
                  {player.action_cards.map((card, i) => (
                    <AnimatedCard key={`a-${i}`} card={card} shouldDeal={i >= newActionStart} />
                  ))}
                  {(player.number_cards.length > 0 || player.modifier_cards.length > 0) && (
                    <span className="w-px bg-green-300/30 mx-0.5" />
                  )}
                </>
              )}
              {player.modifier_cards.map((card, i) => (
                <AnimatedCard key={`m-${i}`} card={card} shouldDeal={i >= newModifierStart} />
              ))}
              {player.number_cards.map((card, i) => (
                <AnimatedCard key={`n-${i}`} card={card} shouldDeal={i >= newNumberStart} />
              ))}
              {player.number_cards.length === 0 &&
                player.modifier_cards.length === 0 &&
                player.action_cards.length === 0 && (
                  <span className="text-xs text-green-300/50 italic">No cards</span>
                )}
            </div>
          </>
        )}
      </div>

      {isTargetable && targetableCardType && TARGETABLE_OVERLAY[targetableCardType] && (
        <div className={cn(
          "absolute inset-0 z-30 rounded flex items-center justify-center animate-pulse",
          TARGETABLE_OVERLAY[targetableCardType].bg
        )}>
          <span className={cn(
            "font-bold tracking-wide uppercase",
            TARGETABLE_OVERLAY[targetableCardType].text,
            compact ? "text-[10px]" : "text-xs md:text-sm"
          )}>
            {TARGETABLE_OVERLAY[targetableCardType].label}
          </span>
        </div>
      )}

      <div className="shrink-0 flex items-center gap-2.5 relative z-20">
        {isDisconnected && !isBusted && (
          <span className={cn(
            "font-bold text-yellow-900 bg-yellow-400 border border-yellow-600 rounded-full",
            compact ? "text-[8px] px-1.5 py-px" : "text-[10px] px-2 py-0.5"
          )}>
            ⏳ Not Responding
          </span>
        )}
        {isBusted && (
          <span className={cn(
            "font-bold text-white bg-red-500 border border-red-300 rounded-full",
            compact ? "text-[8px] px-1.5 py-px" : "text-[10px] px-2 py-0.5"
          )}>
            BUST
          </span>
        )}
        {isFrozen && (
          <span className={cn(
            "font-bold text-white bg-[#0563ce] border border-[#718ee6] rounded-full",
            compact ? "text-[8px] px-1.5 py-px" : "text-[10px] px-2 py-0.5"
          )}>
            FROZEN
          </span>
        )}
        {isStayed && !isFrozen && (
          <span className={cn(
            "font-bold text-blue-300 bg-blue-900/50 rounded",
            compact ? "text-[8px] px-1.5 py-px" : "text-[10px] px-2 py-0.5"
          )}>
            STAYED
          </span>
        )}
        <div className={cn("text-right", (isBusted || isFrozen) && "opacity-30", isTargetable && "opacity-40")}>
          <div className={cn(
            "font-bold text-white font-mono leading-tight",
            compact ? "text-sm" : "text-2xl md:text-3xl"
          )}>
            {player.round_score} pts
          </div>
          <div className={cn("text-green-300/70 font-mono", compact ? "text-[9px]" : "text-sm")}>
            ({totalScore} total)
          </div>
        </div>
      </div>
    </div>
  );
}
